
import { LABELS } from '../../lib/labels'

import type { ProductType } from '../../types'
import type { ReactNode } from 'react'

const BUTTON_WRAPPER_CLASSES = 'flex justify-center items-center gap-2 h-[45px] xl:h-[50px]'

const VIEW_DETAILS_CLASSES = 'h-[30px] md:h-[35px] xl:h-[45px] bg-main-color focus:outline-none focus:bg-main-color active:bg-main-color text-black text-xs md:text-sm xl:text-base font-main-font font-bold w-4/5 xl:w-full hover:bg-main-color transition-all duration-300 ease-in-out'

const OUT_OF_STOCK_CLASSES = 'bg-cards-bg w-full h-auto text-center text-sm border-0 font-montserrat font-bold'

interface ProductButtonProps {
  url: string
  sku: string
  productType?: ProductType
  /** Whether product is in stock */
  isInStock?: boolean
  /** Whether product shows add-to-cart (cu_fara_pret == 'Da') */
  showAddToCart?: boolean
  /** Whether product is explicitly not for sale (cu_fara_pret == 'Nu') */
  isNotForSale?: boolean
  /** Whether product is discontinued (category 470) */
  isDiscontinued?: boolean
  /** Action slot for add-to-cart/wishlist buttons. Falls back to "Vezi detalii" when not provided. */
  actionSlot?: ReactNode
}

/**
 * ProductButton - Action button area matching CardItem.tsx
 *
 * Display logic:
 * 1. Non-simple products → "Vezi detalii" button
 * 2. Simple + can buy + in stock → actionSlot (or "Vezi detalii" fallback)
 * 3. Simple + discontinued → "Produsul nu mai este disponibil"
 * 4. Simple + not for sale → "Produsul nu se mai comercializeaza"
 * 5. Simple + out of stock → "La comanda"
 */
export function ProductButton({
  url,
  productType,
  isInStock = true,
  showAddToCart = true,
  isNotForSale = false,
  isDiscontinued = false,
  actionSlot,
}: ProductButtonProps) {
  const isSimpleProduct = productType === 'simple'

  if (!isSimpleProduct) {
    return (
      <div className={BUTTON_WRAPPER_CLASSES}>
        <ViewDetailsButton url={url} />
      </div>
    )
  }

  // Simple product - check availability
  const canBuy = showAddToCart && isInStock

  if (!canBuy) {
    return (
      <div className={BUTTON_WRAPPER_CLASSES}>
        {isDiscontinued ? (
          <p className={`${OUT_OF_STOCK_CLASSES} xl:text-2xl p-2`}>
            {LABELS.DISCONTINUED}
          </p>
        ) : (
          <p className={OUT_OF_STOCK_CLASSES}>
            {isNotForSale ? LABELS.NOT_FOR_SALE : LABELS.OUT_OF_STOCK}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={BUTTON_WRAPPER_CLASSES}>
      {actionSlot ?? <ViewDetailsButton url={url} />}
    </div>
  )
}

function ViewDetailsButton({ url }: { url: string }) {
  return (
    <button
      className={VIEW_DETAILS_CLASSES}
      onClick={() => { window.location.href = url }}
    >
      <span className="uppercase text-sm md:text-base xl:text-lg font-bold">
        {LABELS.VIEW_DETAILS}
      </span>
    </button>
  )
}
